import { FC, useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Button, CircularProgress, TextField } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { NumericTextBox, NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { State as ODataState } from '@progress/kendo-data-query';
import ItemApi from 'src/api/ItemApi';
import { Item } from 'src/types/itemList';
import { ItemSearchRequest } from 'src/types/itemSearchRequest';
import { Logger } from 'src/utils/logger';

export interface AddedPerfectOrderItem {
  itemNumber: string;
  vendorNumber?: number;
  description?: string;
  quantity: number;
  customerPrice: number;
  kitParentItemNumber?: string;
}

interface Props {
  customerNumber?: string;
  warehouseCode?: string;
  shipToNumber?: string;
  existingItemNumbers: string[];
  disabled?: boolean;
  onAdd: (item: AddedPerfectOrderItem) => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

const itemDefaultPrice = (item: Item): number => (item.listPrice && item.listPrice !== 0 ? item.listPrice : (item.msrp ?? 0));

const PerfectOrderItemPicker: FC<Props> = ({ customerNumber, warehouseCode, shipToNumber, existingItemNumbers, disabled = false, onAdd }) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Item[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Item | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [price, setPrice] = useState<number>(0);
  const [priceLoading, setPriceLoading] = useState(false);
  const searchSeqRef = useRef(0);
  const priceSeqRef = useRef(0);

  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      setSearching(false);
      return;
    }

    const seq = ++searchSeqRef.current;
    setSearching(true);
    const timer = setTimeout(() => {
      const state: ODataState = {
        skip: 0,
        take: 20,
        filter: { logic: 'and', filters: [] },
        sort: [{ field: 'score', dir: 'desc' }],
      };
      const request: ItemSearchRequest = { searchText: trimmed } as ItemSearchRequest;

      ItemApi.getItems(state, request)
        .then((response) => {
          if (seq !== searchSeqRef.current) return;
          setOptions(response.value || []);
        })
        .catch((error) => {
          if (seq !== searchSeqRef.current) return;
          Logger.error('PerfectOrderItemPicker: item search failed', error);
          setOptions([]);
        })
        .finally(() => {
          if (seq === searchSeqRef.current) setSearching(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const fetchPrice = (item: Item, qty: number) => {
    if (!customerNumber || !warehouseCode || qty <= 0) {
      setPrice(itemDefaultPrice(item));
      return;
    }

    const seq = ++priceSeqRef.current;
    setPriceLoading(true);
    ItemApi.getCustomerPrice(item.itemNumber, customerNumber, warehouseCode, qty, itemDefaultPrice(item), shipToNumber ?? '')
      .then((customerPrice) => {
        if (seq !== priceSeqRef.current) return;
        setPrice(Number.isFinite(Number(customerPrice)) ? Number(customerPrice) : itemDefaultPrice(item));
      })
      .catch((error) => {
        if (seq !== priceSeqRef.current) return;
        Logger.error('PerfectOrderItemPicker: getCustomerPrice failed', error);
        setPrice(itemDefaultPrice(item));
      })
      .finally(() => {
        if (seq === priceSeqRef.current) setPriceLoading(false);
      });
  };

  const handleSelect = (_event: unknown, value: Item | null) => {
    setSelected(value);
    setQuantity(1);
    if (value) {
      fetchPrice(value, 1);
    } else {
      priceSeqRef.current++;
      setPriceLoading(false);
      setPrice(0);
    }
  };

  const handleQuantityChange = (e: NumericTextBoxChangeEvent) => {
    const nextQty = e.value ?? 0;
    setQuantity(nextQty);
  };

  const handlePriceChange = (e: NumericTextBoxChangeEvent) => {
    setPrice(e.value ?? 0);
  };

  const reset = () => {
    setSelected(null);
    setInputValue('');
    setOptions([]);
    setQuantity(1);
    setPrice(0);
    priceSeqRef.current++;
    setPriceLoading(false);
  };

  const handleAdd = () => {
    if (!selected || quantity <= 0 || priceLoading) return;
    onAdd({
      itemNumber: selected.itemNumber,
      vendorNumber: selected.vendorNumber,
      description: selected.name,
      quantity,
      customerPrice: price,
    });
    reset();
  };

  const isDuplicate = !!selected && existingItemNumbers.some((n) => n?.toLowerCase() === selected.itemNumber?.toLowerCase());
  const canAdd = !disabled && !!selected && quantity > 0 && !priceLoading && !isDuplicate;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, p: 1, mt: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
      <Autocomplete
        sx={{ flex: '1 1 320px', minWidth: 240 }}
        size="small"
        disabled={disabled}
        options={options}
        value={selected}
        inputValue={inputValue}
        onChange={handleSelect}
        onInputChange={(_e, value, reason) => {
          if (reason !== 'reset') setInputValue(value);
        }}
        filterOptions={(x) => x}
        loading={searching}
        getOptionLabel={(option) => (option ? `${option.itemNumber} — ${option.name ?? ''}` : '')}
        isOptionEqualToValue={(option, value) => option.itemNumber === value.itemNumber}
        noOptionsText={inputValue.trim().length < MIN_SEARCH_LENGTH ? 'Type at least 2 characters' : 'No items found'}
        renderOption={(props, option) => (
          <li {...props} key={option.itemNumber}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ fontWeight: 600 }}>
                {option.itemNumber} — {option.name}
              </Box>
              {option.vendorNumber !== undefined && option.vendorNumber !== null && <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Vendor #{option.vendorNumber}</Box>}
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add Item"
            placeholder="Search by item # or description"
            error={isDuplicate}
            helperText={isDuplicate ? 'Item is already on this perfect order.' : undefined}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searching ? <CircularProgress size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
      <Box sx={{ width: 110 }}>
        <NumericTextBox value={quantity} onChange={handleQuantityChange} min={1} spinners={true} format="n0" width="100%" disabled={disabled || !selected} aria-label="Quantity" />
      </Box>
      <Box sx={{ width: 140, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <NumericTextBox
          value={price}
          onChange={handlePriceChange}
          min={0}
          spinners={false}
          format="c2"
          width="100%"
          disabled={disabled || !selected || priceLoading}
          aria-label="Customer Price"
        />
        {priceLoading && <CircularProgress size={14} />}
      </Box>
      <Button variant="contained" size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleAdd} disabled={!canAdd}>
        Add
      </Button>
    </Box>
  );
};

export default PerfectOrderItemPicker;
